import { API_URL } from "@/config/constants";
import axios, { type AxiosResponse } from "axios";
import {
    IValidatorDetail,
    IValidatorEvalResults,
    IValidatorPerformanceRead,
    IValidatorRunsPage,
    IValidatorStatus,
    IValidatorsResponse,
    IValidatorUpdate,
    ValidatorError,
    ValidatorErrorCode,
    ValidatorRunResultFilter,
} from "./validators.types";

const API = `${API_URL}/admin/validators`;
export const VALIDATOR_REQUEST_TIMEOUT_MS = 30_000;

const TYPED_VALIDATOR_ERROR_CODES = {
    validator_locked: true,
    validator_config_unavailable: true,
    validator_label_catalog_invalid: true,
    validator_label_unknown: true,
    validator_not_found: true,
} as const satisfies Record<ValidatorErrorCode, true>;

export const VALIDATOR_ERROR_CODES = Object.keys(
    TYPED_VALIDATOR_ERROR_CODES
) as ValidatorErrorCode[];

function toTypedValidatorError(error: unknown): ValidatorError | null {
    if (!axios.isAxiosError(error)) {
        return null;
    }
    const data: unknown = error.response?.data;
    if (!data || typeof data !== "object") {
        return null;
    }
    const detail = (data as { detail?: unknown }).detail;
    if (!detail || typeof detail !== "object") {
        return null;
    }
    const code = (detail as { code?: unknown }).code;
    if (
        typeof code !== "string" ||
        !Object.prototype.hasOwnProperty.call(TYPED_VALIDATOR_ERROR_CODES, code)
    ) {
        return null;
    }
    return new ValidatorError(code as ValidatorErrorCode);
}

async function validatorRequest<T>(
    request: Promise<AxiosResponse<T>>
): Promise<T> {
    try {
        const result = await request;
        return result.data;
    } catch (error) {
        throw toTypedValidatorError(error) ?? error;
    }
}

export const listValidators = async (): Promise<IValidatorsResponse> => {
    return validatorRequest(
        axios.get<IValidatorsResponse>(API, {
            timeout: VALIDATOR_REQUEST_TIMEOUT_MS,
        })
    );
};

export const getValidator = async (name: string): Promise<IValidatorDetail> => {
    return validatorRequest(
        axios.get<IValidatorDetail>(`${API}/${encodeURIComponent(name)}`, {
            timeout: VALIDATOR_REQUEST_TIMEOUT_MS,
        })
    );
};

export const getValidatorPerformance = async (
    name: string
): Promise<IValidatorPerformanceRead> =>
    validatorRequest(
        axios.get<IValidatorPerformanceRead>(
            `${API}/${encodeURIComponent(name)}/performance`,
            { timeout: VALIDATOR_REQUEST_TIMEOUT_MS }
        )
    );

export const getValidatorEvalResults = async (
    name: string
): Promise<IValidatorEvalResults> =>
    validatorRequest(
        axios.get<IValidatorEvalResults>(
            `${API}/${encodeURIComponent(name)}/eval-results`,
            { timeout: VALIDATOR_REQUEST_TIMEOUT_MS }
        )
    );

export const getValidatorRuns = async (
    name: string,
    {
        page,
        size,
        result,
    }: { page: number; size: number; result: ValidatorRunResultFilter }
): Promise<IValidatorRunsPage> => {
    return validatorRequest(
        axios.get<IValidatorRunsPage>(
            `${API}/${encodeURIComponent(name)}/runs`,
            {
                params: {
                    page,
                    size,
                    ...(result !== "all" && { result }),
                },
                timeout: VALIDATOR_REQUEST_TIMEOUT_MS,
            }
        )
    );
};

export const updateValidator = async (
    name: string,
    update: IValidatorUpdate
): Promise<IValidatorStatus> => {
    return validatorRequest(
        axios.patch<IValidatorStatus>(
            `${API}/${encodeURIComponent(name)}`,
            update,
            { timeout: VALIDATOR_REQUEST_TIMEOUT_MS }
        )
    );
};
